import {
  BarsArrowDown,
  BarsThree,
  Cash,
  ChartBar,
  ChatBubbleLeftRight,
  CloudArrowUp,
  CogSixTooth,
  CreditCard,
  FlyingBox,
  Gift,
  Clock,
  House,
  ImageSparkle,
  Link,
  ListCheckbox,
  Receipt,
  ReceiptPercent,
  ShoppingBag,
  ShoppingCart,
  Swatch,
  Tag,
  Telescope,
  TruckFast,
  User,
} from "@medusajs/icons"
import type { ComponentType } from "react"
import { tk } from "./i18n"

export type NavGroup = "work" | "customers" | "stock" | "numbers" | "setup"

/**
 * The sections, grouped the way the work is: what is done every day first, the numbers after.
 * Labels are English keys (tk); screens show them through t(). Keywords stay searchable in English,
 * and their Greek (the same key through t()) is searched as well.
 */
export const NAV_GROUPS: Array<{ key: NavGroup; label: string }> = [
  { key: "work", label: tk("Daily work") },
  { key: "customers", label: tk("Customers") },
  { key: "stock", label: tk("Stock & buying") },
  { key: "numbers", label: tk("Numbers") },
  { key: "setup", label: tk("Setup") },
]

export type NavItem = {
  to: string
  label: string
  /** Shorter name for the phone's bottom bar, where there is room for one word. */
  short?: string
  /** One line on the More page saying what is inside, so a section can be found by what it holds. */
  description: string
  icon: ComponentType<{ className?: string }>
  group: NavGroup
  /** In the phone's bottom bar (the rest are under More). */
  tab?: boolean
  /** Other paths that belong to this section (a product's Edit page belongs to Stock). */
  also?: string[]
  /** Words people may search for that are not in the label ("booking" finds Deliveries). */
  keywords?: string
}

export const NAV: NavItem[] = [
  { to: "/", label: tk("Today"), icon: House, group: "work", tab: true, description: tk("What needs doing now, and this month so far") },
  { to: "/orders", label: tk("Orders"), icon: ShoppingCart, group: "work", tab: true, description: tk("Site orders: ship, hand over, collect the cash"), keywords: tk("web site shop box now packing slip refund") },
  { to: "/custom", label: tk("Custom orders"), short: tk("Custom"), icon: Swatch, group: "work", tab: true, description: tk("Orders through Larry: deposits, status, delivery"), keywords: tk("larry sheet deposit") },
  { to: "/booking", label: tk("Deliveries"), icon: TruckFast, group: "work", description: tk("Meet-ups and home deliveries by day, with the route"), keywords: tk("booking bookings meet-up meetup home route day calendar areas") },
  { to: "/courier", label: tk("Courier"), icon: User, group: "work", description: tk("His meet-ups and home deliveries, the cash he holds, what we owe him"), keywords: tk("delivery person driver cash pay handed over settle owed") },
  { to: "/parcels", label: tk("Parcels"), icon: FlyingBox, group: "work", also: ["/buying/parcels"], description: tk("Larry's parcels on the way and arrived"), keywords: tk("shipment tracking dhl arrived larry problem") },
  { to: "/replies", label: tk("Replies"), icon: ChatBubbleLeftRight, group: "customers", description: tk("Ready answers to paste into Instagram DMs"), keywords: tk("dm instagram message template answer") },
  { to: "/payments", label: tk("Payment links"), short: tk("Links"), icon: CreditCard, group: "customers", description: tk("Card links for deposits and balances"), keywords: tk("stripe deposit balance pay link") },
  { to: "/discounts", label: tk("Discounts"), icon: ReceiptPercent, group: "customers", description: tk("Codes customers type at checkout"), keywords: tk("code coupon promo discord") },
  { to: "/stock", label: tk("Stock"), icon: Tag, group: "stock", tab: true, also: ["/products"], description: tk("Every product, sizes in stock, change stock"), keywords: tk("products product sizes inventory new product publish") },
  { to: "/stock-age", label: tk("Stock age"), short: tk("Ageing"), icon: Clock, group: "stock", description: tk("What each unit on the shelf cost, how long it has been there, markdowns to consider"), keywords: tk("fifo batch lot aging old slow dead markdown discount sell-through value") },
  { to: "/catalog-check", label: tk("Catalogue check"), short: tk("Catalogue"), icon: ListCheckbox, group: "stock", description: tk("Products that cannot be bought properly, weak link previews, links that name another product"), keywords: tk("health problems missing photo price preview og image handle test product draft check") },
  { to: "/buying", label: tk("Buying"), icon: ShoppingBag, group: "stock", description: tk("What to order from Larry, and orders sent"), keywords: tk("planner plan purchase order larry bulk") },
  { to: "/shop-order", label: tk("Shop order"), short: tk("Sorting"), icon: BarsArrowDown, group: "stock", description: tk("Which products the shop shows first, by drag and drop"), keywords: tk("order sort rank arrange position first top home page άμεσα διαθέσιμα catalogue") },
  { to: "/extras", label: tk("Extras"), icon: Gift, group: "stock", description: tk("Choices beside the size, like the packaging, and their prices"), keywords: tk("packaging box premium standard options add-on") },
  { to: "/finder", label: tk("Supplier finder"), short: tk("Finder"), icon: ImageSparkle, group: "stock", description: tk("Find an item in the suppliers' catalogues from a photo"), keywords: tk("yupoo photo picture reverse image search find match taobao weidian stock sizes") },
  { to: "/catalogue", label: tk("Catalogue site"), short: tk("Lookbook"), icon: Swatch, group: "stock", description: tk("The suppliers' products cleaned up for the catalogue site: what is ready, what to check"), keywords: tk("catalogue site lookbook products photos cut-out background lv badge value top tier code") },
  { to: "/suppliers", label: tk("Suppliers"), icon: Link, group: "stock", description: tk("Where each item is bought, with size guides"), keywords: tk("album yupoo weidian links size guide") },
  { to: "/analytics", label: tk("Analytics"), icon: ChartBar, group: "numbers", description: tk("Sales, customers, products and checks"), keywords: tk("sales revenue profit customers sizes report") },
  { to: "/money", label: tk("Money"), icon: Cash, group: "numbers", description: tk("Where the money is, month by month, Larry's account"), keywords: tk("ledger expenses p&l larry balance cash") },
  { to: "/forecast", label: tk("Forecast"), icon: Telescope, group: "numbers", description: tk("The next three months, from your own numbers"), keywords: tk("future prediction break-even") },
  { to: "/costs", label: tk("Costs"), icon: Receipt, group: "numbers", description: tk("What each product cost you, and delivery costs"), keywords: tk("landed cost rules margin") },
  { to: "/messages", label: tk("Messages"), icon: ChatBubbleLeftRight, group: "setup", description: tk("The ready messages to customers, in your own words"), keywords: tk("dm instagram text template wording reply message customer") },
  { to: "/settings", label: tk("Settings"), icon: CogSixTooth, group: "setup", description: tk("Language, notifications, delivery costs, connections"), keywords: tk("push notifications phone costs email language greek english") },
  { to: "/import", label: tk("Import data"), short: tk("Import"), icon: CloudArrowUp, group: "setup", description: tk("Shopify history and the partner's app export"), keywords: tk("shopify csv export partner") },
]

/** The phone's fifth tab: every section, grouped, with search. */
export const MORE = { to: "/more", label: tk("More"), icon: BarsThree }

/** The section a path belongs to (the longest match wins, so /buying/parcels is Parcels, not Buying). */
export function sectionFor(pathname: string): NavItem | undefined {
  let best: NavItem | undefined
  let bestLength = -1
  for (const item of NAV) {
    for (const path of [item.to, ...(item.also ?? [])]) {
      const matches = path === "/" ? pathname === "/" : pathname === path || pathname.startsWith(`${path}/`)
      if (matches && path.length > bestLength) {
        best = item
        bestLength = path.length
      }
    }
  }
  return best
}
